import { Divider, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Chart from './components/Chart';
import { Aside, Callout } from './components/Callout';
import DayTimeline, { DayTimelineEvent } from './components/DayTimeline';
import { CodeSandbox, Embed, Gist, Tweet, YouTube } from './components/embeds';
import Figure from './components/Figure';
import Gallery from './components/Gallery';
import Split from './components/Split';
import { useResolveAsset } from './context';

// Prose furniture as styled() — these wrap raw markdown output (every post
// uses them) so they belong in named, theme-aware components, not scattered sx.
const Pre = styled('pre')(({ theme }) => ({
  background: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  overflowX: 'auto',
  fontSize: '0.9rem',
}));

const InlineCode = styled('code')(({ theme }) => ({
  background: theme.palette.action.hover,
  padding: theme.spacing(0.25, 0.75),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.9em',
}));

const Quote = styled('blockquote')(({ theme }) => ({
  margin: theme.spacing(3, 0),
  paddingLeft: theme.spacing(3),
  borderLeft: `4px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
}));

const Table = styled('table')(({ theme }) => ({
  borderCollapse: 'collapse',
  width: '100%',
  margin: theme.spacing(3, 0),
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 1.5),
    textAlign: 'left',
  },
  '& th': { background: theme.palette.action.hover },
}));

const heading =
  (variant: 'h3' | 'h4' | 'h5' | 'h6', mt: number) =>
  ({ children }: { children?: React.ReactNode }) =>
    (
      <Typography variant={variant} sx={{ mt, mb: 1.5, clear: 'both' }}>
        {children}
      </Typography>
    );

// react-markdown / MDX hand `code` both inline and block; `pre` wraps blocks.
const Code: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className }) =>
  className ? <code className={className}>{children}</code> : <InlineCode>{children}</InlineCode>;

const Anchor: React.FC<{ href?: string; children?: React.ReactNode }> = ({ href = '', children }) => {
  const internal = href.startsWith('/');
  if (internal) {
    return (
      <Link component={RouterLink} to={href}>
        {children}
      </Link>
    );
  }
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  );
};

// Structural, reused for every inline image / video in every post → styled()
// over inline style, and theme-aware border radius to match <Figure>.
const Img = styled('img')({
  display: 'block',
  maxWidth: '100%',
  height: 'auto',
});

const Video = styled('video')(({ theme }) => ({
  display: 'block',
  width: '100%',
  height: 'auto',
  margin: theme.spacing(3, 0),
  borderRadius: theme.shape.borderRadius,
}));

// Markdown `![alt](path)` with one of these extensions renders a player.
const VIDEO_EXT = /\.(mov|mp4|m4v|webm|ogg)$/i;

// Plain markdown `![alt](images/x.jpg)` / `![alt](videos/x.mp4)` both map here
// and get slug-relative resolution, so authors never hardcode a CDN URL even
// without <Figure>. A video extension becomes a <video> player; the alt text
// is the accessible label, and the anchor is fallback for browsers that can't
// play the source.
const Media: React.FC<{ src?: string; alt?: string }> = ({ src = '', alt = '' }) => {
  const resolveAsset = useResolveAsset();
  const resolved = resolveAsset(src);
  if (VIDEO_EXT.test(src)) {
    return (
      <Video src={resolved} controls preload="metadata" playsInline aria-label={alt || undefined}>
        <Anchor href={resolved}>Download the video</Anchor>
      </Video>
    );
  }
  return <Img src={resolved} alt={alt} loading="lazy" />;
};

/**
 * The component map handed to the evaluated MDX module. Lowercase keys
 * override default markdown HTML; PascalCase keys are the rich components an
 * author can drop into prose by name.
 */
export const mdxComponents = {
  h1: heading('h3', 0),
  h2: heading('h4', 5),
  h3: heading('h5', 4),
  h4: heading('h6', 3),
  h5: heading('h6', 2),
  h6: heading('h6', 2),
  p: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="body1" sx={{ my: 2, lineHeight: 1.7 }}>
      {children}
    </Typography>
  ),
  a: Anchor,
  img: Media,
  hr: () => <Divider sx={{ my: 4 }} />,
  blockquote: ({ children }: { children?: React.ReactNode }) => <Quote>{children}</Quote>,
  pre: ({ children }: { children?: React.ReactNode }) => <Pre>{children}</Pre>,
  code: Code,
  table: ({ children }: { children?: React.ReactNode }) => <Table>{children}</Table>,
  // Rich components, referenced by name in MDX:
  Figure,
  Gallery,
  Split,
  Chart,
  Callout,
  Aside,
  DayTimeline,
  DayTimelineEvent,
  YouTube,
  Tweet,
  CodeSandbox,
  Gist,
  Embed,
};

export type MdxComponents = typeof mdxComponents;
